import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, Button, Badge, EmptyState, Skeleton, ErrorState } from '@/components/ui'
import { Mail, Clock, X, RefreshCw } from 'lucide-react'
import { api, type InviteInfo } from '@/api/client'

// Helper to format role string as display name
function formatRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1)
}

export function TeamInvitesPage() {
  const { orgSlug } = useParams()
  const [invites, setInvites] = useState<InviteInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchInvites = async () => {
      if (!orgSlug) return
      try {
        setError(null)
        const response = await api.orgs.listInvitations(orgSlug)
        setInvites(response.invites)
      } catch (err) {
        console.error('Failed to fetch invites:', err)
        setError(err instanceof Error ? err.message : 'Failed to load invitations')
      } finally {
        setIsLoading(false)
      }
    }
    fetchInvites()
  }, [orgSlug])

  const handleResend = async (inviteId: string) => {
    if (!orgSlug) return
    try {
      const response = await api.orgs.resendInvitation(orgSlug, inviteId)
      // Update the invite in the list with new expiry
      setInvites(invites.map(i => i.id === inviteId ? response.invite : i))
    } catch (err) {
      console.error('Failed to resend invitation:', err)
    }
  }

  const handleRevoke = async (inviteId: string) => {
    if (!orgSlug) return
    try {
      await api.orgs.cancelInvitation(orgSlug, inviteId)
      setInvites(invites.filter(i => i.id !== inviteId))
    } catch (err) {
      console.error('Failed to cancel invitation:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div>
                    <Skeleton className="h-4 w-48 mb-2" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return <ErrorState title="Failed to load invitations" description={error} action={<Button onClick={() => window.location.reload()}>Try Again</Button>} />
  }

  // Filter by status
  const pendingInvites = invites.filter(i => i.status === 'pending')
  const expiredInvites = invites.filter(i => i.status === 'expired')

  return (
    <div className="space-y-6">
      {invites.length === 0 ? (
        <EmptyState icon={Mail} title="No pending invites" description="All invitations have been accepted or expired" />
      ) : (
        <>
          {pendingInvites.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-[var(--text-secondary)]">Pending ({pendingInvites.length})</h3>
              {pendingInvites.map((invite) => (
                <Card key={invite.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-elevated-2)]">
                          <Mail className="h-5 w-5 text-[var(--text-secondary)]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">{invite.email}</p>
                          <p className="text-xs text-[var(--text-muted)]">Invited by {invite.invited_by} on {new Date(invite.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge>{formatRole(invite.role)}</Badge>
                        <div className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
                          <Clock className="h-3 w-3" />
                          Expires {new Date(invite.expires_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" title="Resend" onClick={() => handleResend(invite.id)}><RefreshCw className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" title="Revoke" onClick={() => handleRevoke(invite.id)}><X className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {expiredInvites.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-[var(--text-secondary)]">Expired ({expiredInvites.length})</h3>
              {expiredInvites.map((invite) => (
                <Card key={invite.id} className="opacity-60">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-elevated-2)]">
                          <Mail className="h-5 w-5 text-[var(--text-muted)]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">{invite.email}</p>
                          <p className="text-xs text-[var(--text-muted)]">Invited by {invite.invited_by} on {new Date(invite.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant="default">{formatRole(invite.role)}</Badge>
                        <Badge variant="error">Expired</Badge>
                        <Button variant="secondary" size="sm" onClick={() => handleResend(invite.id)}>
                          <RefreshCw className="mr-2 h-4 w-4" />Resend
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
